import React, { useState } from 'react';
import { BsTelephone } from 'react-icons/bs';

import {
  Column,
  ErrorTag,
  ErrorText,
  Fields,
  FormBody,
  OutlineButton,
  PrimaryButton,
  TextInput,
} from 'components/layout/Auth/AuthForm/styles';

import {
  CONTENT_WIDTH,
  NarrowActions,
  NarrowField,
  OnboardingIntro,
  OnboardingSubtitle,
  OnboardingTitle,
} from '../styles';
import { splitFullName } from '../splitFullName';

type DadosPessoaisProps = {
  fullName: string;
  email: string;
  phone: string;
  loading: boolean;
  erro: string | null;
  onChangeFullName: (valor: string) => void;
  onChangeEmail: (valor: string) => void;
  onChangePhone: (valor: string) => void;
  onSubmit: () => void;
  onBack: () => void;
};

const emailValido = (valor: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor.trim());

const mascaraTelefone = (valor: string): string => {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);

  if (digitos.length <= 2) return digitos;
  if (digitos.length <= 3)
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  if (digitos.length <= 7)
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 3)} ${digitos.slice(
      3
    )}`;

  return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 3)} ${digitos.slice(
    3,
    7
  )}-${digitos.slice(7)}`;
};

const DadosPessoais = ({
  fullName,
  email,
  phone,
  loading,
  erro,
  onChangeFullName,
  onChangeEmail,
  onChangePhone,
  onSubmit,
  onBack,
}: DadosPessoaisProps): JSX.Element => {
  const [tocado, setTocado] = useState(false);

  const { name, lastName } = splitFullName(fullName);
  const nomeIncompleto = tocado && !!fullName && !lastName;
  const emailInvalido = tocado && !!email && !emailValido(email);
  const telefoneInvalido = tocado && phone.replace(/\D/g, '').length < 11;

  const podeEnviar =
    !loading &&
    !!name &&
    !!lastName &&
    emailValido(email) &&
    phone.replace(/\D/g, '').length === 11;

  const handlePhone = (event: React.ChangeEvent<HTMLInputElement>): void =>
    onChangePhone(mascaraTelefone(event.target.value));

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setTocado(true);
    if (podeEnviar) onSubmit();
  };

  return (
    <Column gap={24} width={CONTENT_WIDTH}>
      <OnboardingIntro>
        <OnboardingTitle>
          Queremos agilizar seu processo! É simples e rápido.
        </OnboardingTitle>
        <OnboardingSubtitle>
          Agora iremos preencher seus dados cadastrais
        </OnboardingSubtitle>
      </OnboardingIntro>

      <form onSubmit={handleSubmit} noValidate>
        <FormBody>
          <Fields>
            <NarrowField>
              <TextInput
                id="nome-completo"
                name="nome-completo"
                type="text"
                label="Nome completo"
                placeholder="Digite"
                required
                fullWidth
                autoFocus
                error={nomeIncompleto}
                value={fullName}
                onChange={(event) => onChangeFullName(event.target.value)}
                onBlur={() => setTocado(true)}
              />
              {nomeIncompleto && (
                <ErrorText>Informe o nome e o sobrenome.</ErrorText>
              )}
            </NarrowField>

            <NarrowField>
              <TextInput
                id="email"
                name="email"
                type="email"
                label="E-mail"
                placeholder="Digite"
                required
                fullWidth
                error={emailInvalido}
                value={email}
                onChange={(event) => onChangeEmail(event.target.value)}
                onBlur={() => setTocado(true)}
              />
              {emailInvalido && (
                <ErrorText>Endereço de e-mail inválido.</ErrorText>
              )}
            </NarrowField>

            <NarrowField>
              <TextInput
                id="celular"
                name="celular"
                type="tel"
                inputMode="numeric"
                label="Celular"
                placeholder="(99) 9 9999-9999"
                required
                fullWidth
                error={telefoneInvalido}
                value={phone}
                onChange={handlePhone}
                onBlur={() => setTocado(true)}
                trailingIcon={<BsTelephone aria-hidden="true" />}
              />
              {telefoneInvalido && (
                <ErrorText>Informe um celular válido com DDD.</ErrorText>
              )}
            </NarrowField>
          </Fields>

          {!!erro && <ErrorTag role="alert">{erro}</ErrorTag>}

          <NarrowActions>
            <OutlineButton
              type="button"
              variant="secondary"
              shape="pill"
              fullWidth
              onClick={onBack}
            >
              Voltar
            </OutlineButton>
            <PrimaryButton
              type="submit"
              shape="pill"
              fullWidth
              disabled={!podeEnviar}
            >
              {loading ? 'Aguarde...' : 'Continuar'}
            </PrimaryButton>
          </NarrowActions>
        </FormBody>
      </form>
    </Column>
  );
};

export default DadosPessoais;
